import React, { useState, useEffect } from "react";
import { 
    User, 
    Calendar as CalendarIcon, 
    MapPin, 
    Mail, 
    Phone, 
    Home, 
    CreditCard, 
    CheckCircle, 
    ArrowRight, 
    Loader2, 
    AlertCircle, 
    ChevronDown, 
    Utensils, 
    Info,
    Trophy
} from "lucide-react";
import API_URL from "../config";

const RegistrationForm = () => {
    const [categories, setCategories] = useState([]);
    const [prices, setPrices] = useState({});
    const [fixedPriceCategories, setFixedPriceCategories] = useState({});
    const [formData, setFormData] = useState({
        category: null,
        participants: [],
        rental: false,
        billingInfo: { firstName: "", lastName: "", street: "", city: "", postalCode: "", email: "", phone: "" },
        note: "",
        agreeToTerms: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchCategoriesAndPrices = async () => {
            try {
                const [catsRes, pricesRes] = await Promise.all([
                    fetch(`${API_URL}/categories`),
                    fetch(`${API_URL}/prices`)
                ]);
                const catsData = await catsRes.json();
                const pricesData = await pricesRes.json();
                setCategories(catsData);
                setPrices(pricesData);
                setFixedPriceCategories(pricesData.fixed_price_categories || {});
            } catch (error) {
                console.error("Failed to fetch data:", error);
            }
        };
        fetchCategoriesAndPrices();
    }, []);

    useEffect(() => {
        if (formData.category && !formData.category.rental_available) {
            setFormData(prev => ({ ...prev, rental: false }));
        }
    }, [formData.category]);

    const handleCategoryChange = (e) => {
        const id = parseInt(e.target.value);
        const cat = categories.find(c => c.id === id);
        if (cat) {
            const participants = Array.from({ length: cat.crew_size_max }, () => ({
                name: "",
                birthDate: "",
                city: "",
            }));
            setFormData({ ...formData, category: cat, participants });
        }
    };

    const handleParticipantChange = (index, field, value) => {
        const updated = [...formData.participants];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, participants: updated });
    };

    const handleBillingChange = (field, value) => {
        setFormData({
            ...formData,
            billingInfo: { ...formData.billingInfo, [field]: value },
        });
    };

    const calculateTotal = () => {
        const cat = formData.category;
        if (!cat) return 0;
        if (fixedPriceCategories[cat.id]) {
            const fixed = fixedPriceCategories[cat.id];
            return (fixed.crew_size * (prices.start_fee_per_person || 15)) + (fixed.canoe_rentals * (prices.canoe_rental_fee || 15));
        }
        return (formData.participants.length * (prices.start_fee_per_person || 15)) + (formData.rental ? (prices.canoe_rental_fee || 15) : 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        try {
            const res = await fetch(`${API_URL}/maraton-register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setIsSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const data = await res.json();
                setErrorMessage(data.error || "Chyba pri odosielaní.");
            }
        } catch (error) {
            setErrorMessage("Server neodpovedá.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="bg-white/5 backdrop-blur-2xl border-2 border-white/20 rounded-[2rem] p-6 md:p-12 text-center shadow-2xl overflow-hidden">
                <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl animate-bounce">
                    <CheckCircle className="w-10 h-10 text-blue-900" />
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-widest mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Registrácia prijatá!
                </h2>
                <p className="text-white/70 text-base md:text-lg mb-10 max-w-md mx-auto leading-relaxed px-4">
                    Ďakujeme! Čoskoro vám do e-mailu doručíme potvrdenie a pokyny k úhrade štartovného.
                </p>
                <div className="space-y-4 px-4 overflow-hidden">
                    <button 
                        onClick={() => setIsSubmitted(false)}
                        className="w-full sm:w-auto bg-yellow-400 text-blue-900 font-black px-8 py-4 rounded-xl uppercase tracking-widest hover:bg-yellow-300 transition-all transform active:scale-95"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                        Nová registrácia
                    </button>
                    <button 
                        onClick={() => window.location.href = "/"}
                        className="w-full sm:w-auto mt-4 sm:mt-0 sm:ml-4 bg-white/10 text-white border border-white/20 font-black px-8 py-4 rounded-xl uppercase tracking-widest hover:bg-white/20 transition-all"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                        Domov
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12 max-w-full overflow-x-hidden">
            {/* Cenník Info */}
            <div className="bg-blue-900/40 backdrop-blur-md rounded-2xl p-6 border-l-4 border-yellow-400 shadow-xl">
                <div className="flex flex-col md:flex-row gap-6 md:gap-12 justify-center items-center">
                    <div className="flex flex-col items-center text-center">
                        <span className="text-white/60 text-xs font-black uppercase tracking-widest mb-1">Štartovné na osobu</span>
                        <span className="text-3xl font-black text-white drop-shadow-md">{prices.start_fee_per_person || 0} €</span>
                    </div>
                    <div className="hidden md:block w-px h-12 bg-white/10"></div>
                    <div className="flex flex-col items-center text-center">
                        <span className="text-white/60 text-xs font-black uppercase tracking-widest mb-1">Prenájom kanoe (voliteľné)</span>
                        <span className="text-3xl font-black text-cyan-400 drop-shadow-md">{prices.canoe_rental_fee || 0} €</span>
                    </div>
                    <div className="hidden md:block w-px h-12 bg-white/10"></div>
                    <div className="flex flex-col items-center text-center">
                        <span className="text-white/60 text-xs font-black uppercase tracking-widest mb-1">Katamarán (fixný poplatok)</span>
                        <span className="text-3xl font-black text-orange-400 drop-shadow-md">{prices.catamaran_rental_fee || 0} €</span>
                    </div>
                </div>
            </div>

            {/* Step 1: Kategória */}
            <div className="bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-[2rem] p-4 md:p-10 shadow-2xl relative overflow-hidden">
                <div className="flex items-center gap-4 mb-8 px-2">
                    <div className="p-3 bg-yellow-400 rounded-2xl shadow-lg shadow-yellow-400/30">
                        <Trophy className="w-5 h-5 md:w-6 md:h-6 text-blue-900" />
                    </div>
                    <h3 className="text-lg md:text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>1. Výber Kategórie</h3>
                </div>

                <div className="relative group px-2 max-w-full">
                    <select
                        value={formData.category?.id || ""}
                        onChange={handleCategoryChange}
                        required
                        className="w-full appearance-none bg-blue-950 border-2 border-white/30 text-white font-black uppercase tracking-widest rounded-2xl px-4 md:px-6 py-5 focus:outline-none focus:border-yellow-400 transition-all cursor-pointer shadow-xl text-xs md:text-base pr-12 box-border"
                    >
                        <option value="" disabled className="bg-blue-950">Vyberte kategóriu...</option>
                        {categories.filter(c => c.is_public).map(c => (
                            <option key={c.id} value={c.id} className="bg-blue-950 text-white py-4 font-bold text-sm">
                                {c.name.toUpperCase()}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 text-yellow-400 pointer-events-none transition-transform group-focus-within:rotate-180" />
                </div>
            </div>

            {/* Step 2: Posádka */}
            {formData.category && (
                <div className="bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-[2rem] p-4 md:p-10 shadow-2xl animate-in slide-in-from-top-10 duration-500 overflow-hidden">
                    <div className="flex items-center gap-4 mb-8 md:mb-10 px-2">
                        <div className="p-3 bg-cyan-400 rounded-2xl shadow-lg shadow-cyan-400/30">
                            <User className="w-5 h-5 md:w-6 md:h-6 text-blue-900" />
                        </div>
                        <h3 className="text-lg md:text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>2. Údaje o posádke</h3>
                    </div>

                    <div className="space-y-6 md:space-y-10 px-2">
                        {formData.participants.map((p, idx) => (
                            <div key={idx} className="bg-blue-950/40 rounded-3xl p-5 md:p-8 border-2 border-white/10 relative shadow-inner overflow-hidden">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center border-2 border-blue-950 text-[10px] font-black text-blue-900 uppercase tracking-widest shadow-lg">
                                        {idx + 1}
                                    </div>
                                    <h4 className="text-white font-black uppercase tracking-widest text-sm md:text-base border-b-2 border-cyan-400 pb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Účastník</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 overflow-hidden">
                                    <InputField 
                                        label="Meno a priezvisko" 
                                        value={p.name} 
                                        onChange={v => handleParticipantChange(idx, 'name', v)} 
                                        required={idx < formData.category.crew_size_min} 
                                        placeholder="Peter Pádlar"
                                        accent="cyan"
                                        icon={<User className="w-4 h-4" />}
                                    />
                                    <InputField 
                                        label="Dátum narodenia" 
                                        type="date"
                                        value={p.birthDate} 
                                        onChange={v => handleParticipantChange(idx, 'birthDate', v)} 
                                        required={idx < formData.category.crew_size_min}
                                        max={new Date().toISOString().split('T')[0]}
                                        accent="cyan"
                                        icon={<CalendarIcon className="w-4 h-4" />}
                                    />
                                    <InputField 
                                        label="Bydlisko (Mesto)" 
                                        value={p.city} 
                                        onChange={v => handleParticipantChange(idx, 'city', v)} 
                                        required={idx < formData.category.crew_size_min}
                                        placeholder="napr. Nová Dedinka"
                                        accent="cyan"
                                        icon={<MapPin className="w-4 h-4" />}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {formData.category.rental_available && (
                        <div className="mt-10 mx-2 p-5 bg-blue-950/60 rounded-[2rem] border-2 border-white/20 flex flex-col md:flex-row items-center justify-between gap-4 group cursor-pointer hover:border-yellow-400 transition-all shadow-xl overflow-hidden" onClick={() => setFormData(f => ({...f, rental: !f.rental}))}>
                            <div className="flex items-center gap-4 w-full md:w-auto">
                                <div className={`shrink-0 w-10 h-10 rounded-xl border-2 transition-all flex items-center justify-center shadow-lg ${formData.rental ? 'bg-yellow-400 border-yellow-400' : 'bg-blue-900/40 border-white/30'}`}>
                                    {formData.rental && <CheckCircle className="w-6 h-6 text-blue-900" />}
                                </div>
                                <div>
                                    <h4 className="text-white font-black uppercase tracking-widest text-sm md:text-base">Prenájom kanoe</h4>
                                    <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Loď aj pádla</p>
                                </div>
                            </div>
                            <span className="text-yellow-400 font-black text-2xl md:text-3xl drop-shadow-lg">+ {prices.canoe_rental_fee || 15} €</span>
                        </div>
                    )}
                </div>
            )}

            {/* Step 3: Fakturácia */}
            <div className="bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-[2rem] p-4 md:p-10 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-4 mb-10 px-2">
                    <div className="p-3 bg-orange-500 rounded-2xl shadow-lg shadow-orange-500/30">
                        <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg md:text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>3. Fakturačné Údaje</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-2 overflow-hidden">
                    <InputField label="Krstné Meno" value={formData.billingInfo.firstName} onChange={v => handleBillingChange('firstName', v)} required icon={<User className="w-4 h-4" />} />
                    <InputField label="Priezvisko" value={formData.billingInfo.lastName} onChange={v => handleBillingChange('lastName', v)} required icon={<User className="w-4 h-4" />} />
                    <div className="md:col-span-2">
                        <InputField label="Ulica a číslo" value={formData.billingInfo.street} onChange={v => handleBillingChange('street', v)} required icon={<Home className="w-4 h-4" />} />
                    </div>
                    <InputField label="Mesto" value={formData.billingInfo.city} onChange={v => handleBillingChange('city', v)} required icon={<MapPin className="w-4 h-4" />} />
                    <InputField label="PSČ" value={formData.billingInfo.postalCode} onChange={v => handleBillingChange('postalCode', v)} required icon={<MapPin className="w-4 h-4" />} />
                    <InputField label="Váš E-mail" value={formData.billingInfo.email} onChange={v => handleBillingChange('email', v)} required type="email" icon={<Mail className="w-4 h-4" />} />
                    <InputField label="Telefónne číslo" value={formData.billingInfo.phone} onChange={v => handleBillingChange('phone', v)} required type="tel" icon={<Phone className="w-4 h-4" />} />
                </div>

                <div className="mt-10 mx-2 space-y-3">
                    <label className="text-[10px] font-black text-white/60 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <Info className="w-4 h-4 text-orange-500" /> Poznámka k objednávke
                    </label>
                    <textarea
                        value={formData.note}
                        onChange={e => setFormData({...formData, note: e.target.value})}
                        rows="4"
                        className="w-full bg-blue-950 border-2 border-white/30 text-white font-bold rounded-[1.5rem] px-4 md:px-6 py-5 focus:outline-none focus:border-orange-500 transition-all resize-none placeholder:text-white/20 text-sm md:text-base box-border shadow-inner"
                        placeholder="Máte špeciálnu požiadavku?"
                    />
                </div>
            </div>

            {/* Step 4: Súhlas a Odoslanie */}
            <div className="bg-white/5 backdrop-blur-xl border-2 border-white/10 rounded-[2rem] p-4 md:p-10 shadow-2xl overflow-hidden mb-20">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-10 px-2 overflow-hidden">
                    <div className="flex-grow w-full lg:w-auto space-y-8 overflow-hidden">
                        <div className="flex items-center gap-5 cursor-pointer group" onClick={() => setFormData(f => ({...f, agreeToTerms: !f.agreeToTerms}))}>
                            <div className={`shrink-0 w-12 h-12 rounded-2xl border-4 transition-all flex items-center justify-center ${formData.agreeToTerms ? 'bg-yellow-400 border-yellow-400' : 'bg-blue-950 border-white/40 ring-2 ring-white/10'}`}>
                                {formData.agreeToTerms && <CheckCircle className="w-8 h-8 text-blue-900" />}
                            </div>
                            <p className="text-white font-bold text-xs md:text-base leading-relaxed overflow-hidden">
                                Súhlasím s <a href="https://klubmalydunaj.sk/podmienky" target="_blank" rel="noreferrer" className="text-yellow-400 hover:text-yellow-300 font-black decoration-dotted underline underline-offset-4">podmienkami podujatia</a>.
                            </p>
                        </div>
                        
                        <div className="bg-blue-950 rounded-[2rem] p-6 md:p-8 border-2 border-yellow-400/20 flex flex-col md:flex-row items-center justify-between gap-4 shadow-2xl overflow-hidden">
                            <div className="text-center md:text-left">
                                <span className="block text-white/40 font-black uppercase tracking-widest text-[10px] mb-2">Celková suma</span>
                                <span className="text-3xl md:text-4xl font-black text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]">{calculateTotal()} €</span>
                            </div>
                            <CreditCard className="hidden md:block w-12 h-12 text-yellow-400/10" />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={!formData.agreeToTerms || isLoading}
                        className={`w-full lg:w-auto px-8 md:px-20 py-6 md:py-10 rounded-[1.5rem] md:rounded-[3rem] font-black uppercase tracking-widest text-base md:text-2xl flex items-center justify-center gap-5 transition-all transform active:scale-95 shadow-2xl ${
                            formData.agreeToTerms && !isLoading 
                            ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300 shadow-yellow-400/40' 
                            : 'bg-white/10 text-white/20 cursor-not-allowed border-2 border-white/5'
                        }`}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                        {isLoading ? (
                            <Loader2 className="w-7 h-7 md:w-8 md:h-8 animate-spin" />
                        ) : (
                            <>Odoslať <ArrowRight className="w-7 h-7 md:w-8 md:h-8" /></>
                        )}
                    </button>
                </div>

                {errorMessage && (
                    <div className="mt-8 mx-2 p-5 bg-red-500/20 border-2 border-red-500 rounded-2xl flex items-center gap-4 text-red-200 font-black text-[10px] md:text-sm uppercase tracking-widest overflow-hidden">
                        <AlertCircle className="w-6 h-6 shrink-0" />
                        {errorMessage}
                    </div>
                )}
            </div>
        </form>
    );
};

const InputField = ({ label, value, onChange, required, type = "text", icon, placeholder, accent = "orange", max }) => (
    <div className="space-y-3 w-full">
        <label className="text-[10px] font-black text-white/60 uppercase tracking-widest ml-1 block">{label} {required && '*'}</label>
        <div className="relative group w-full overflow-hidden">
            {icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-400/70 group-focus-within:text-yellow-400 transition-colors z-10">{icon}</div>}
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                required={required}
                placeholder={placeholder}
                max={max}
                className={`w-full md:w-full bg-blue-950 border-2 border-white/30 text-white font-bold rounded-[1.25rem] md:rounded-2xl shadow-xl transition-all placeholder:text-white/20 text-xs md:text-base box-border block px-4 md:px-6 ${icon ? 'pl-11 md:pl-14' : ''} py-4 md:py-5 ${
                    accent === 'cyan' ? 'focus:border-cyan-400' : 'focus:border-orange-500'
                } [color-scheme:dark]`}
            />
        </div>
    </div>
);

export default RegistrationForm;
